import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import {
    FaBan,
    FaCheck,
    FaDownload,
    FaEdit,
    FaEnvelope,
    FaExclamationTriangle,
    FaEye,
    FaFilter,
    FaPhone,
    FaPlus,
    FaSearch,
    FaTrash,
    FaUserCheck,
    FaUserClock,
    FaUsers
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('view'); // view, edit, create
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const { apiCall } = useAuth();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        stiffness: 300, 
        damping: 24 
      }
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, filterStatus]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apiCall('GET', '/api/admin/users');
      const userArray = response.data?.users || response.data?.data?.users || [];
      if (userArray.length) {
        // Ensure each user has a `name` field for downstream UI logic
        const processedUsers = userArray.map(u => {
          const fallbackName = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
          return {
            ...u,
            name: u.name || fallbackName || u.email, // final fallback to email
            isVerified: u.isVerified ?? u.isEmailVerified ?? true
          };
        });
        setUsers(processedUsers);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.includes(searchTerm)
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(user => {
        switch (filterStatus) {
          case 'active':
            return user.isActive && user.isVerified;
          case 'inactive':
            return !user.isActive;
          case 'unverified':
            return !user.isVerified;
          case 'suspended':
            return user.isSuspended;
          default:
            return true;
        }
      });
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  };

  const handleStatusChange = async (userId, action) => {
    try {
      const response = await apiCall('PUT', `/api/admin/users/${userId}/status`, { action });
      if (response.success) {
        fetchUsers();
      }
    } catch (error) {
      console.error('Failed to update user status:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        const response = await apiCall('DELETE', `/api/admin/users/${userId}`);
        if (response.success) {
          fetchUsers();
        }
      } catch (error) {
        console.error('Failed to delete user:', error);
      }
    }
  };

  const openModal = (type, user = null) => {
    setModalType(type);
    setSelectedUser(user);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setModalType('view');
  };

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const getStatusBadge = (user) => {
    if (user.isSuspended) return <span className="luxury-badge-danger flex items-center"><FaBan className="mr-1 h-3 w-3" /> Suspended</span>;
    if (!user.isVerified) return <span className="luxury-badge-warning flex items-center"><FaExclamationTriangle className="mr-1 h-3 w-3" /> Unverified</span>;
    if (!user.isActive) return <span className="luxury-badge-danger flex items-center"><FaBan className="mr-1 h-3 w-3" /> Inactive</span>;
    return <span className="luxury-badge-success flex items-center"><FaCheck className="mr-1 h-3 w-3" /> Active</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <motion.div 
          initial={{ rotate: 0 }}
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600"
        ></motion.div>
      </div>
    );
  }

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="space-y-8"
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-luxury-900 font-montserrat tracking-tight">Users Management</h1>
          <p className="text-luxury-600 mt-1 font-poppins">Manage all registered users on the platform</p>
        </div>
        <div className="flex items-center space-x-3">
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => openModal('create')}
            className="luxury-button-primary text-sm"
          >
            <FaPlus className="mr-2" />
            Add User
          </motion.button>
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="luxury-button-secondary text-sm"
          >
            <FaDownload className="mr-2" />
            Export
          </motion.button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div 
        variants={containerVariants}
        className="grid grid-cols-1 md:grid-cols-4 gap-6"
      >
        <motion.div
          variants={itemVariants}
          whileHover={{ 
            y: -5,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
          className="bg-white rounded-2xl shadow-lg shadow-primary-500/20 border border-luxury-200/50 p-6 transition-all duration-500"
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-luxury-600 font-poppins">Total Users</p>
              <p className="text-3xl font-bold text-luxury-900 font-montserrat mt-2">{users.length}</p>
            </div>
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg shadow-primary-500/30">
              <FaUsers className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="h-1 w-full bg-luxury-200/50 rounded-full mt-4">
            <div className="h-1 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full" style={{width: '100%'}}></div>
          </div>
        </motion.div>
        
        <motion.div
          variants={itemVariants}
          whileHover={{ 
            y: -5,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
          className="bg-white rounded-2xl shadow-lg shadow-success-500/20 border border-luxury-200/50 p-6 transition-all duration-500"
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-luxury-600 font-poppins">Active Users</p>
              <p className="text-3xl font-bold text-luxury-900 font-montserrat mt-2">
                {users.filter(u => u.isActive && u.isVerified).length}
              </p>
            </div>
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-success-500 to-success-700 flex items-center justify-center shadow-lg shadow-success-500/30">
              <FaUserCheck className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="h-1 w-full bg-luxury-200/50 rounded-full mt-4">
            <div className="h-1 bg-gradient-to-r from-success-500 to-success-600 rounded-full" 
              style={{width: `${users.length ? (users.filter(u => u.isActive && u.isVerified).length / users.length) * 100 : 0}%`}}></div>
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          whileHover={{ 
            y: -5,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
          className="bg-white rounded-2xl shadow-lg shadow-warning-500/20 border border-luxury-200/50 p-6 transition-all duration-500"
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-luxury-600 font-poppins">Unverified</p>
              <p className="text-3xl font-bold text-luxury-900 font-montserrat mt-2">
                {users.filter(u => !u.isVerified).length}
              </p>
            </div>
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-warning-500 to-warning-700 flex items-center justify-center shadow-lg shadow-warning-500/30">
              <FaUserClock className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="h-1 w-full bg-luxury-200/50 rounded-full mt-4">
            <div className="h-1 bg-gradient-to-r from-warning-500 to-warning-600 rounded-full" 
              style={{width: `${users.length ? (users.filter(u => !u.isVerified).length / users.length) * 100 : 0}%`}}></div>
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          whileHover={{ 
            y: -5,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
          className="bg-white rounded-2xl shadow-lg shadow-danger-500/20 border border-luxury-200/50 p-6 transition-all duration-500"
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-luxury-600 font-poppins">Suspended</p>
              <p className="text-3xl font-bold text-luxury-900 font-montserrat mt-2">
                {users.filter(u => u.isSuspended).length}
              </p>
            </div>
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-danger-500 to-danger-700 flex items-center justify-center shadow-lg shadow-danger-500/30">
              <FaBan className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="h-1 w-full bg-luxury-200/50 rounded-full mt-4">
            <div className="h-1 bg-gradient-to-r from-danger-500 to-danger-600 rounded-full" 
              style={{width: `${users.length ? (users.filter(u => u.isSuspended).length / users.length) * 100 : 0}%`}}></div>
          </div>
        </motion.div>
      </motion.div>

      {/* Search & Filter */}
      <motion.div 
        variants={itemVariants}
        className="bg-white rounded-2xl shadow-lg border border-luxury-200/50 p-6"
      >
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-grow">
            <FaSearch className="absolute left-1 top-1/2 transform -translate-y-1/2 text-luxury-400" />
            <input
              type="text"
              placeholder="Search users by name, email or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-luxury-50/50 border border-luxury-200 rounded-xl text-luxury-900 placeholder-luxury-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
            />
          </div>
          <div className="flex gap-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-4 pr-10 py-3 bg-luxury-50/50 border border-luxury-200 rounded-xl text-luxury-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 cursor-pointer"
            >
              <option value="all">All Users</option>
              <option value="active">Active Users</option>
              <option value="inactive">Inactive Users</option>
              <option value="unverified">Unverified Users</option>
              <option value="suspended">Suspended Users</option>
            </select>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => fetchUsers()}
              className="px-5 py-3 bg-primary-100 text-primary-700 font-medium rounded-xl hover:bg-primary-200 transition-all duration-300 flex items-center justify-center"
            >
              <FaFilter className="mr-2" />
              Apply
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Users Table */}
      <motion.div 
        variants={itemVariants}
        className="bg-white rounded-2xl shadow-lg border border-luxury-200/50 overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-luxury-50 to-luxury-100">
                <th className="px-6 py-4 text-left text-sm font-semibold text-luxury-900 border-b border-luxury-200 whitespace-nowrap font-nunito">User</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-luxury-900 border-b border-luxury-200 whitespace-nowrap font-nunito">Contact</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-luxury-900 border-b border-luxury-200 whitespace-nowrap font-nunito">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-luxury-900 border-b border-luxury-200 whitespace-nowrap font-nunito">Created</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-luxury-900 border-b border-luxury-200 whitespace-nowrap font-nunito">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-luxury-100">
              {currentUsers.length > 0 ? (
                currentUsers.map((user) => (
                  <motion.tr 
                    key={user._id || user.id}
                    whileHover={{ backgroundColor: 'rgba(244, 245, 247, 0.5)' }}
                    className="transition-all duration-200 isolation-auto"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0 bg-primary-100 rounded-full flex items-center justify-center">
                          {user.avatar ? (
                            <img src={user.avatar} alt={user.name} className="h-10 w-10 rounded-full object-cover" />
                          ) : (
                            <span className="text-primary-700 font-semibold font-montserrat">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-luxury-900 font-nunito">{user.name}</p>
                          <p className="text-sm text-luxury-500 font-nunito">{user.role || 'User'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className="flex items-center text-luxury-900 font-nunito">
                          <FaEnvelope className="mr-2 text-luxury-500" size={12} />
                          {user.email}
                        </div>
                        {user.phone && (
                          <div className="flex items-center text-luxury-500 mt-1 font-nunito">
                            <FaPhone className="mr-2 text-luxury-500" size={12} />
                            {user.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(user)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-luxury-500 font-nunito">
                        {new Date(user.createdAt).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end space-x-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => openModal('view', user)}
                          className="icon-button text-primary-600 hover:text-primary-800 hover:bg-primary-100/50 rounded-full"
                          title="View Details"
                        >
                          <FaEye />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => openModal('edit', user)}
                          className="icon-button text-warning-600 hover:text-warning-800 hover:bg-warning-100/50 rounded-full"
                          title="Edit User"
                        >
                          <FaEdit />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleDeleteUser(user._id || user.id)}
                          className="icon-button text-danger-600 hover:text-danger-800 hover:bg-danger-100/50 rounded-full"
                          title="Delete User"
                        >
                          <FaTrash />
                        </motion.button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center">
                    <div className="flex flex-col items-center">
                      <FaUsers className="h-8 w-8 text-luxury-400" />
                      <p className="mt-2 text-luxury-600 font-medium">No users found</p>
                      <p className="text-luxury-500 text-sm">Try adjusting your search or filter criteria</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 bg-luxury-50/50 border-t border-luxury-200 flex justify-between items-center">
            <span className="text-sm text-luxury-600 font-nunito">
              Showing {indexOfFirstUser + 1}-{Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length} users
            </span>
            <div className="flex space-x-1">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  currentPage === 1 
                  ? 'bg-luxury-100 text-luxury-400 cursor-not-allowed' 
                  : 'bg-luxury-100 text-luxury-700 hover:bg-luxury-200'
                }`}
              >
                Previous
              </motion.button>
              
              {Array.from({ length: Math.min(5, totalPages) }).map((_, idx) => {
                // Logic to show the current page and pages around it
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = idx + 1;
                } else if (currentPage <= 3) {
                  pageNum = idx + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + idx;
                } else {
                  pageNum = currentPage - 2 + idx;
                }
                
                return (
                  <motion.button
                    key={idx}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 rounded-md text-sm font-medium ${
                      currentPage === pageNum 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-luxury-100 text-luxury-700 hover:bg-luxury-200'
                    }`}
                  >
                    {pageNum}
                  </motion.button>
                );
              })}
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  currentPage === totalPages 
                  ? 'bg-luxury-100 text-luxury-400 cursor-not-allowed' 
                  : 'bg-luxury-100 text-luxury-700 hover:bg-luxury-200'
                }`}
              >
                Next
              </motion.button>
            </div>
          </div>
        )}
      </motion.div>

      {/* User Modal */}
      {showModal && (
        <div className="luxury-modal">
          <div className="luxury-modal-content">
            {/* Modal content based on modalType */}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Users; 